import { useSetAtom } from 'jotai'
import React from 'react'
import * as R from 'remeda'
import { z } from 'zod'

import { brandGroupIdAtom } from '~/atoms'
import { exists, setValueIfExist } from '~/utils'

import {
  brandIdsAtom,
  customerAttributeIdsAtom,
  dataDisplayTypeAtom,
  fiveSegmentCommentAtom,
  fiveSegmentHiddenColumnKeysAtom,
  fiveSegmentSortValueAtom,
  hiddenRowIdsAtom,
  nineSegmentCommentAtom,
  nineSegmentHiddenColumnKeysAtom,
  nineSegmentSortValueAtom,
  questionIdsAtom,
  savedFiveSegmentCommentAtom,
  savedNineSegmentCommentAtom,
  segmentDisplayTypeAtom,
} from '../../atoms'

import { schema1 } from './schemas/schema1'
import { schema2 } from './schemas/schema2'

const schema = z.union([schema1, schema2])

export const useRestoreParams = () => {
  const setBrandGroupId = useSetAtom(brandGroupIdAtom)

  const setDataDisplayType = useSetAtom(dataDisplayTypeAtom)
  const setSegmentDisplayType = useSetAtom(segmentDisplayTypeAtom)
  const setBrandIds = useSetAtom(brandIdsAtom)
  const setQuestionIds = useSetAtom(questionIdsAtom)
  const setCustomerAttributeIds = useSetAtom(customerAttributeIdsAtom)
  const setFiveSegmentComment = useSetAtom(fiveSegmentCommentAtom)
  const setSavedFiveSegmentComment = useSetAtom(savedFiveSegmentCommentAtom)

  const setFiveSegmentSortValue = useSetAtom(fiveSegmentSortValueAtom)
  const setNineSegmentComment = useSetAtom(nineSegmentCommentAtom)
  const setSavedNineSegmentComment = useSetAtom(savedNineSegmentCommentAtom)
  const setNineSegmentSortValue = useSetAtom(nineSegmentSortValueAtom)

  const setHiddenRowIds = useSetAtom(hiddenRowIdsAtom)

  const setFiveSegmentHiddenColumnKeys = useSetAtom(
    fiveSegmentHiddenColumnKeysAtom,
  )
  const setNineSegmentHiddenColumnKeys = useSetAtom(
    nineSegmentHiddenColumnKeysAtom,
  )

  return React.useCallback(
    (state: unknown) => {
      const parsed = schema.safeParse(state)

      if (!parsed.success) {
        return null
      }
      const params = parsed.data

      setValueIfExist(params.brandGroupId, setBrandGroupId)
      setValueIfExist(params.dataDisplayType, setDataDisplayType)
      setValueIfExist(params.segmentDisplayType, setSegmentDisplayType)
      setValueIfExist(params.brandIds, setBrandIds)
      setValueIfExist(params.questionIds, setQuestionIds)
      setValueIfExist(params.customerAttributeIds, setCustomerAttributeIds)
      if (exists(params.fiveSegmentComment) && exists(params.hash)) {
        const hash = params.hash
        const comment = params.fiveSegmentComment
        setFiveSegmentComment((prev) => ({ ...prev, [hash]: comment }))
        setSavedFiveSegmentComment((prev) => ({ ...prev, [hash]: comment }))
      }
      setValueIfExist(params.fiveSegmentSortValue, setFiveSegmentSortValue)
      if (exists(params.nineSegmentComment) && exists(params.hash)) {
        const hash = params.hash
        const comment = params.nineSegmentComment
        setNineSegmentComment((prev) => ({ ...prev, [hash]: comment }))
        setSavedNineSegmentComment((prev) => ({ ...prev, [hash]: comment }))
      }
      setValueIfExist(params.nineSegmentSortValue, setNineSegmentSortValue)
      setValueIfExist(params.hiddenRowIds, setHiddenRowIds)
      setValueIfExist(
        params.fiveSegmentHiddenColumnKeys,
        setFiveSegmentHiddenColumnKeys,
      )
      setValueIfExist(
        params.nineSegmentHiddenColumnKeys,
        setNineSegmentHiddenColumnKeys,
      )

      return R.pick(params, ['hash'])
    },
    [
      setBrandGroupId,
      setBrandIds,
      setCustomerAttributeIds,
      setDataDisplayType,
      setFiveSegmentComment,
      setFiveSegmentHiddenColumnKeys,
      setFiveSegmentSortValue,
      setHiddenRowIds,
      setNineSegmentComment,
      setNineSegmentHiddenColumnKeys,
      setNineSegmentSortValue,
      setQuestionIds,
      setSavedFiveSegmentComment,
      setSavedNineSegmentComment,
      setSegmentDisplayType,
    ],
  )
}
